import { Line } from 'react-chartjs-2';
import { IItemBrandPricingComparative } from '@/types/api/pricingsComparativeRegion';
import { formatCurrency } from '@/utils';
import {
  formatTicks,
  generateChartData,
  generateLabels,
  setPointRadius,
} from '@/utils/components/priceComparativeChartModal';
import { Box, Typography } from '@mui/material';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  TooltipItem,
  Tick,
  ChartOptions,
} from 'chart.js';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);
interface IPriceLineChartProps {
  comparative?: IItemBrandPricingComparative;
}

const PriceLineChart = ({ comparative }: IPriceLineChartProps) => {
  const { average, lower } = generateChartData(comparative?.comparison_history);
  const labels = generateLabels(comparative?.comparison_history);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Preço médio',
        data: average,
        borderColor: '#FF7A00',
        backgroundColor: '#FF7A00',
        borderWidth: 2,
      },
      {
        label: 'Menor preço',
        data: lower,
        borderColor: '#00D7D9',
        backgroundColor: '#00D7D9',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: 2,
    elements: {
      line: {
        tension: 0.3,
      },
    },
    datasets: {
      line: {
        pointRadius: setPointRadius,
      },
    },
    plugins: {
      tooltip: {
        usePointStyle: true,
        intersect: false,
        cornerRadius: 4,
        mode: 'index' as const,
        position: 'nearest' as const,
        callbacks: {
          label: (tooltipItem: TooltipItem<'line'>) =>
            formatCurrency(tooltipItem.parsed.y ?? 0),
          labelPointStyle: () => ({
            pointStyle: 'circle' as const,
            rotation: 0,
          }),
        },
      },
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'line',
        },
      },
    },
    scales: {
      y: {
        min: 0,
        ticks: {
          precision: 0,
          callback: (value: string | number) => formatCurrency(Number(value)),
        },
      },
      x: {
        ticks: {
          callback: (value: string | number, index: number, values: Tick[]) =>
            formatTicks(value, index, values, labels),
        },
      },
    },
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {comparative && comparative.comparison_history.length > 0 && (
        <Line data={chartData} options={chartOptions} />
      )}
      {!comparative?.comparison_history.length && (
        <Box sx={{ width: '100%', textAlign: 'center', pt: 4 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Não há dados para exibir
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PriceLineChart;
export type { IPriceLineChartProps };
